/* cspell:ignore nics */
import get from 'lodash/get';

import { sharedMemoryFromSystemDevices } from '@/delivery-type-plugin/capabilities/shared-memory';

import { type VolumeItem, VolumeMountTab, VolumeMountType } from '../../../capabilities/container-storage/volumeTypes';
import { deserializeServiceListFromApi } from '../../../capabilities/service/serviceApiField';
import { apiTokensToStartupArgFormRows } from '../../../capabilities/startup-command-args/utils';
import { normalizeContainerDeployConfigStorage } from '../../../platform/container-deploy-config';
import { parseGiSize } from '../../../utils/diskSize';
import {
  isImagePresetVolume,
  mergeEnvVarsWithDeployConfigConstraints,
  mergeStartupArgsWithDeployConfigConstraints,
} from '../../_shared/common/container-deploy-config/containerDeployConfig';
import { buildCiDirectResourceModulesFromForm } from '../scenes';
import { toFormDnsNameservers, toFormDnsSearches } from './dnsForm';

function toCommaText(values: string[] | undefined): string | undefined {
  return values?.length ? values.join(',') : undefined;
}

function fromCommaText(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String);
  if (raw == null || String(raw).trim() === '') return [];
  return String(raw)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function readRequirementValue(raw: unknown): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'object' && raw !== null) {
    const req = (raw as { request?: unknown; limit?: unknown }).request ?? (raw as { request?: unknown; limit?: unknown }).limit;
    return readRequirementValue(req);
  }
  const text = String(raw).trim().replace(/Gi$/i, '');
  const num = Number(text);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

/** API Requirement / Gi 字符串 → 表单 flat Gi 数字 */
export function normalizeMemoryForForm(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'number') return raw > 0 ? raw : undefined;
  return readRequirementValue(raw);
}

/** API card map → 表单 GPU 扁平结构 */
export function normalizeGpuFromApi(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const gpu = raw as Record<string, unknown>;
  if (!gpu.vendor && !gpu.device && !gpu.vendorID && !gpu.deviceID && !gpu.vendorId && !gpu.deviceId) {
    return undefined;
  }

  const cardRaw = gpu.card;
  let cardName: string | undefined;
  let cardRequest: unknown;

  if (cardRaw && typeof cardRaw === 'object' && !Array.isArray(cardRaw)) {
    const cardObj = cardRaw as Record<string, unknown>;
    if ('name' in cardObj || 'request' in cardObj) {
      cardName = cardObj.name != null ? String(cardObj.name) : undefined;
      cardRequest = cardObj.request ?? cardObj.limit;
    } else {
      const [name, requirement] = Object.entries(cardObj)[0] ?? [];
      if (name) {
        cardName = name;
        cardRequest =
          requirement && typeof requirement === 'object'
            ? ((requirement as { request?: unknown; limit?: unknown }).request ??
              (requirement as { request?: unknown; limit?: unknown }).limit)
            : requirement;
      }
    }
  }

  const number = cardRequest ?? gpu.number;

  return {
    vendor: gpu.vendor,
    device: gpu.device,
    vendorID: gpu.vendorID ?? gpu.vendorId,
    deviceID: gpu.deviceID ?? gpu.deviceId,
    number,
    name: gpu.name ?? cardName,
    code: gpu.code,
    driverVersion: gpu.driverVersion,
    cudaVersion: gpu.cudaVersion,
    ...(cardName != null
      ? {
          card: {
            name: cardName,
            request: number,
          },
        }
      : {}),
  };
}

function readVolumeSizeGi(raw: unknown): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'object' && raw !== null) {
    const req = (raw as { request?: unknown }).request;
    return req != null ? parseGiSize(req) : undefined;
  }
  return parseGiSize(raw);
}

/** API resource.volume → 表单 volumes + systemDiskSize */
export function normalizeVolumesFromApi(apiVolumes: unknown, containerName?: string): { systemDiskSize?: number; volumes: VolumeItem[] } {
  if (!Array.isArray(apiVolumes)) {
    return { volumes: [] };
  }

  const volumes: VolumeItem[] = [];
  let systemDiskSize: number | undefined;

  apiVolumes.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const vol = item as Record<string, unknown>;
    const sizeGi = readVolumeSizeGi(vol.size);
    const source = vol.source as Record<string, unknown> | undefined;
    const mountPath = vol.mountPath != null ? String(vol.mountPath) : undefined;

    if (vol.type === 'System') {
      if (sizeGi != null) systemDiskSize = sizeGi;
      return;
    }

    if (source?.type === 'PersistentVolumeClaimTemplate') {
      const volume: VolumeItem = {
        type: 'VolumeClaimTemplate',
        volumeName: vol.name != null ? String(vol.name) : undefined,
        storageClass: (vol.storageclass ?? vol.storageClassName) as string | undefined,
        accessModes: vol.accessModes as string[] | undefined,
        volumeCapacity: sizeGi,
        reclaimPolicy: vol.reclaimPolicy as string | undefined,
        volumeMounts: mountPath
          ? [
              {
                mountPath,
                mountType: VolumeMountType.ReadWriteMany,
                containerName: containerName ?? '',
              },
            ]
          : undefined,
      };
      if (isImagePresetVolume(volume)) {
        (volume as VolumeItem & { _fromImage?: boolean })._fromImage = true;
      }
      volumes.push(volume);
      return;
    }

    volumes.push({
      type: VolumeMountTab.PERSISTENCE,
      selected: source?.claimName != null ? String(source.claimName) : '',
      volumeName: vol.name != null ? String(vol.name) : '',
      volumeHostPath: mountPath ?? '',
      reclaimPolicy: vol.reclaimPolicy as string | undefined,
      volumeMounts: mountPath
        ? [
            {
              mountPath,
              mountType: VolumeMountType.ReadWriteMany,
              containerName: containerName ?? '',
            },
          ]
        : undefined,
    });
  });

  return { systemDiskSize, volumes };
}

function readNamedResource(raw: unknown): { name: string } | undefined {
  if (raw == null || raw === '') return undefined;
  if (typeof raw === 'string') return { name: raw };
  if (typeof raw === 'object' && raw !== null && 'name' in raw) {
    const name = String((raw as { name?: unknown }).name ?? '').trim();
    return name ? { name } : undefined;
  }
  return undefined;
}

function normalizeBandwidthForForm(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  const text = String(raw).replace(/Mbps/i, '').trim();
  const num = Number(text);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

function normalizeEnvForForm(raw: unknown): { name: string; value: string; _self: string }[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as { name?: string; key?: string; value?: unknown };
      const name = String(row.name ?? row.key ?? '').trim();
      if (!name) return null;
      const value = row.value != null ? String(row.value) : '';
      return {
        name,
        value,
        _self: JSON.stringify({ custom: true, editable: true, visible: true, key: name }),
      };
    })
    .filter((item): item is { name: string; value: string; _self: string } => Boolean(item));
}

function normalizeSystemForFormImage(system: Record<string, unknown>) {
  const commandArr = Array.isArray(system.command) ? system.command.map(String) : fromCommaText(system.command);
  const argsArr = Array.isArray(system.args) ? system.args.map(String) : fromCommaText(system.args);
  const startupArgs = apiTokensToStartupArgFormRows(argsArr);

  return {
    key: system.imageID ?? system.imageId ?? system.url ?? system.image,
    url: system.url ?? system.image,
    image: system.image ?? system.url,
    name: system.name ?? system.imageName ?? system.url ?? system.image,
    imageName: system.name ?? system.imageName,
    os: system.os,
    version: system.version,
    command: toCommaText(commandArr),
    args: toCommaText(argsArr),
    startupArgs: startupArgs.length ? startupArgs : undefined,
    env: normalizeEnvForForm(system.env),
  };
}

/** 将 ContainerInstance 详情 API（metadata + spec）转为插件表单值 */
export function hydrateContainerInstanceDetailToForm(detail: Record<string, unknown>, namespace: string): Record<string, unknown> {
  const metadata = (detail.metadata ?? {}) as Record<string, unknown>;
  const spec = (detail.spec ?? detail) as Record<string, unknown>;
  const resource = (spec.resource ?? {}) as Record<string, unknown>;
  const network = (resource.network ?? {}) as Record<string, unknown>;
  const system = (spec.system ?? {}) as Record<string, unknown>;
  const instanceName = String(metadata.name ?? spec.name ?? '').trim();
  const workspaceRaw = spec.workspace;
  const workspace = Array.isArray(workspaceRaw) ? workspaceRaw[0] : workspaceRaw;

  const cpuReq = readRequirementValue(get(resource, 'cpu.request') ?? get(resource, 'cpu.limit') ?? resource.cpu);
  const memoryGi = normalizeMemoryForForm(get(resource, 'memory.request') ?? get(resource, 'memory.limit') ?? resource.memory);
  const { systemDiskSize, volumes } = normalizeVolumesFromApi(resource.volume, instanceName);
  const gpu = normalizeGpuFromApi(resource.gpu);
  const systemOptions = (system.options ?? {}) as Record<string, unknown>;
  const storedDeployConfig = normalizeContainerDeployConfigStorage(systemOptions.containerDeployConfig);
  const basicImage = normalizeSystemForFormImage(system);
  const startupArgs = storedDeployConfig
    ? mergeStartupArgsWithDeployConfigConstraints(basicImage.startupArgs ?? [], storedDeployConfig)
    : basicImage.startupArgs;
  const envWithConstraints = storedDeployConfig
    ? mergeEnvVarsWithDeployConfigConstraints(basicImage.env ?? [], storedDeployConfig)
    : basicImage.env;

  const dnsConfig = (network.dnsConfig ?? {}) as Record<string, unknown>;
  const bandwidth = normalizeBandwidthForForm(network.egressRate ?? network.bandwidth ?? network.Bandwidth);
  const services = deserializeServiceListFromApi((network.services ?? network.service) as unknown[] | undefined);

  const sharedMemoryForm = sharedMemoryFromSystemDevices(system.devices as Record<string, unknown> | undefined);
  const hasNics = Array.isArray(network.nics) && network.nics.length > 0;

  const formValues: Record<string, unknown> = {
    name: spec.name ?? metadata.name,
    description: spec.description,
    workspace,
    namespace: metadata.namespace ?? namespace,
    datacenter: spec.datacenter,
    runStrategy: spec.runStrategy ?? 'Always',
    systemDiskSize,
    image: basicImage.url,
    imageNickname: spec.name,
    basic: {
      image: {
        ...basicImage,
        startupArgs,
        env: envWithConstraints,
      },
    },
    system: {
      ...system,
      command: basicImage.command,
      args: basicImage.args,
      env: envWithConstraints,
      ...(storedDeployConfig ? { options: { containerDeployConfig: storedDeployConfig } } : {}),
    },
    network: { nics: network.nics },
    resource: {
      cpu: cpuReq != null ? { request: cpuReq, limit: cpuReq } : undefined,
      memory: memoryGi,
      gpu,
      network: {
        vpc: readNamedResource(network.vpc),
        subnet: readNamedResource(network.subnet) ?? { name: 'default' },
        securitygroup: readNamedResource(network.securitygroup ?? network.securityGroup),
        services,
        Bandwidth: bandwidth,
        egressRate: bandwidth,
        dnsConfig: {
          nameservers: toFormDnsNameservers(dnsConfig.nameservers),
          searches: toFormDnsSearches(dnsConfig.searches),
          options: dnsConfig.options,
        },
      },
    },
    volumes,
    ...(sharedMemoryForm ? { _containerDeploySharedMemory: sharedMemoryForm, _sharedMemoryEnabled: true } : {}),
    ...(hasNics ? { _nicEnabled: true } : {}),
    ...(system.registry && typeof system.registry === 'object' ? { registry: system.registry } : {}),
  };

  formValues.skuResourceModules = buildCiDirectResourceModulesFromForm(formValues);

  return formValues;
}
